import React, { useState } from "react";
import Button from "../components/Button";
import GlassCard from "../components/GlassCard";
import Badge from "../components/Badge";

export default function HomeView({ onNavigate, onOpenWhy, onOpenBooking }) {
  const [signed, setSigned] = useState(false);

  return (
    <div className="container" style={{ display: "grid", gap: 32 }}>
      {/* 1. HERO SECTION: House Build + Local Trust */}
      <section className="hero">
        <div
          className="glass highlight"
          style={{
            padding: "48px 24px",
            textAlign: "center",
            position: "relative",
            boxShadow: "var(--sh-md)",
            overflow: "hidden",
          }}
        >
          {/* Subtle background glow */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: "50%",
              width: 1,
              height: "100%",
              transform: "translateX(-50%)",
              background:
                "linear-gradient(180deg, transparent, rgba(34,197,94,.3), transparent)",
              filter: "blur(40px)",
            }}
          />

          <div className="chip" style={{ margin: "0 auto 20px" }}>
            <span className="dot" /> BUILD THE FOUNDATION
          </div>

          <h1 style={{ marginBottom: "20px", lineHeight: "1.1" }}>
            Build a body you can{" "}
            <span
              style={{
                background: "linear-gradient(90deg, #34D399, #22D3EE)",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              live in.
            </span>
          </h1>

          <p
            className="muted"
            style={{
              fontSize: 18,
              maxWidth: 680,
              margin: "0 auto 24px",
              lineHeight: "1.6",
            }}
          >
            Overwhelmed by diets, gadgets, and random workouts? I help busy
            people start with the big rocks—assessment, mobility, strength, and
            simple nutrition—built in the right order so it lasts.
          </p>

          <div
            style={{
              fontSize: "14px",
              color: "var(--brand)",
              fontWeight: "600",
              marginBottom: "32px",
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >
            IN-HOME TRAINING (YOURS OR MINE) + REMOTE COACHING
          </div>

          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Button onClick={() => onNavigate("plan")}>See the Plan</Button>
            <Button variant="secondary" onClick={onOpenWhy}>
              Why this works
            </Button>
            <Button
              onClick={() => {
                window.dispatchEvent(
                  new CustomEvent("track", {
                    detail: { action: "open_booking", source: "hero" },
                  })
                );
                onOpenBooking();
              }}
            >
              Book Now
            </Button>
          </div>
        </div>
      </section>

      {/* 2. THE BIG ROCKS (Replaces "Verified Proof") */}
      <section>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 16,
          }}
        >
          <div className="dot" />
          <div
            className="muted"
            style={{
              fontSize: 12,
              letterSpacing: 1,
              textTransform: "uppercase",
              fontWeight: 800,
            }}
          >
            The Big Rocks We Track
          </div>
        </div>

        <div className="cards">
          {[
            {
              k: "Consistency",
              v: "3–5",
              sub: "Sessions per week. A repeatable plan you can actually complete.",
              tone: "verified",
              badge: "TARGET",
            },
            {
              k: "Pain & Joints",
              v: "0–3",
              sub: "Pain scale (out of 10). We train around pain so you don’t stall out.",
              tone: "verified",
              badge: "TRACKED",
            },
            {
              k: "Protein Baseline",
              v: "30–40g",
              sub: "Per meal target. The building material for the house.",
              tone: "neutral",
              badge: "GOAL",
            },
          ].map((c, i) => (
            <GlassCard key={i}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <div className="muted" style={{ fontSize: 14 }}>
                  {c.k}
                </div>
                <Badge tone={c.tone}>{c.badge}</Badge>
              </div>
              <div
                style={{
                  fontFamily: "ui-monospace,Menlo,monospace",
                  fontWeight: 800,
                  fontSize: 34,
                  marginBottom: 6,
                }}
              >
                {c.v}
              </div>
              <div
                className="muted"
                style={{
                  fontSize: 13,
                  borderTop: "1px solid var(--line)",
                  paddingTop: 12,
                  lineHeight: "1.4",
                }}
              >
                {c.sub}
              </div>
            </GlassCard>
          ))}
        </div>
      </section>

      {/* 3. HOW IT WORKS (Micro-Sequence) */}
      <section>
        <div
          className="cards"
          style={{
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          }}
        >
          {[
            {
              step: "01",
              title: "Survey",
              desc: "We assess constraints, pain history, and movement.",
            },
            {
              step: "02",
              title: "Clear Land",
              desc: "Mobility + sleep rhythm + protein baseline.",
            },
            {
              step: "03",
              title: "Build",
              desc: "Core + movement patterns + progressive overload.",
            },
          ].map((item, i) => (
            <div key={i} className="glass" style={{ padding: "20px" }}>
              <div
                style={{
                  fontSize: "12px",
                  color: "var(--brand)",
                  fontWeight: "800",
                  marginBottom: "8px",
                }}
              >
                STEP {item.step}
              </div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "#fff",
                  marginBottom: "8px",
                }}
              >
                {item.title}
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: "var(--muted)",
                  lineHeight: "1.5",
                }}
              >
                {item.desc}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 4. THE HOUSE RULES (Replaces Oath) */}
      <section
        className="glass"
        style={{ textAlign: "center", padding: "32px 24px" }}
      >
        <h3 style={{ marginBottom: 16 }}>The House Rules</h3>
        <div
          className="muted"
          style={{
            fontStyle: "italic",
            marginBottom: 24,
            fontSize: "16px",
            lineHeight: "1.8",
          }}
        >
          <p>“I start where I am.”</p>
          <p>“I build the foundation first.”</p>
          <p>“I stack small wins until it’s normal.”</p>
        </div>
        <button
          onClick={() => setSigned((s) => !s)}
          className={`btn ${signed ? "primary" : "secondary"}`}
          aria-pressed={signed}
          style={{ minWidth: "200px" }}
        >
          {signed ? "✓ COMMITMENT MADE" : "MAKE THE COMMITMENT"}
        </button>
      </section>
    </div>
  );
}
